import { createFileRoute } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";

import { runContentHealth } from "@/lib/content-health.functions";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin/halsa")({
  head: () => ({
    meta: [
      { title: "Innehållshälsa — AK Husbil & Fritid" },
      { name: "description", content: "Internt verktyg som crawlar sajtens SSR-svar och hittar SEO-brister." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: HealthPage,
});

const severityStyle: Record<string, string> = {
  error: "bg-destructive/15 text-destructive border-destructive/30",
  warn: "bg-amber-500/15 text-amber-700 border-amber-500/30 dark:text-amber-400",
  info: "bg-muted text-muted-foreground border-border",
};

function HealthPage() {
  const crawl = useServerFn(runContentHealth);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(40);

  const mutation = useMutation({
    mutationFn: (vars: { limit: number; offset: number }) => crawl({ data: vars }),
  });

  const data = mutation.data;
  const problemPages = data?.pages.filter((p) => p.issues.some((i) => i.severity !== "info")) ?? [];

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-10">
      <h1 className="text-2xl font-bold tracking-tight">Innehållshälsa</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Crawlar sajtens egna SSR-svar från sitemap.xml och kontrollerar titlar, meta descriptions,
        canonical, H1, ordmängd, OG-taggar och JSON-LD — exakt det Google ser.
      </p>

      <div className="mt-6 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="block text-muted-foreground">Start (offset)</span>
          <input
            type="number"
            min={0}
            value={offset}
            onChange={(e) => setOffset(Math.max(0, Number(e.target.value) || 0))}
            className="mt-1 w-28 rounded-md border border-border bg-background px-3 py-2"
          />
        </label>
        <label className="text-sm">
          <span className="block text-muted-foreground">Antal (max 120)</span>
          <input
            type="number"
            min={1}
            max={120}
            value={limit}
            onChange={(e) => setLimit(Math.min(120, Math.max(1, Number(e.target.value) || 1)))}
            className="mt-1 w-28 rounded-md border border-border bg-background px-3 py-2"
          />
        </label>
        <Button
          onClick={() => mutation.mutate({ limit, offset })}
          disabled={mutation.isPending}
          className="min-h-11"
        >
          {mutation.isPending ? "Crawlar…" : "Kör kontroll"}
        </Button>
      </div>

      {mutation.isError && (
        <p className="mt-4 text-sm text-destructive">
          Kunde inte köra kontrollen: {(mutation.error as Error).message}
        </p>
      )}

      {data && (
        <section className="mt-8 space-y-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: "URL:er i sitemap", value: data.total },
              { label: "Kontrollerade", value: data.pages.length },
              { label: "Fel", value: data.errors },
              { label: "Varningar", value: data.warnings },
            ].map((s) => (
              <div key={s.label} className="rounded-lg border border-border p-4">
                <div className="text-2xl font-semibold">{s.value}</div>
                <div className="text-xs text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>

          <div>
            <h2 className="text-lg font-semibold">Sammanfattning per kod</h2>
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.entries(data.byCode)
                .sort((a, b) => b[1] - a[1])
                .map(([code, count]) => (
                  <span key={code} className="rounded-full border border-border px-3 py-1 text-xs">
                    {code} · {count}
                  </span>
                ))}
              {Object.keys(data.byCode).length === 0 && (
                <span className="text-sm text-muted-foreground">Inga avvikelser i det här intervallet.</span>
              )}
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold">Sidor med avvikelser ({problemPages.length})</h2>
            <ul className="mt-3 space-y-3">
              {problemPages.map((p) => (
                <li key={p.url} className="rounded-lg border border-border p-4">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <a href={p.url} className="font-medium underline break-all" target="_blank" rel="noreferrer">
                      {new URL(p.url).pathname}
                    </a>
                    <span className="text-xs text-muted-foreground">
                      HTTP {p.status} · {p.words} ord · {p.ms} ms
                      {p.edgeCache ? ` · cache ${p.edgeCache}` : ""}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {p.issues.map((i) => (
                      <span
                        key={i.code}
                        className={`rounded-md border px-2 py-1 text-xs ${severityStyle[i.severity]}`}
                      >
                        {i.code}: {i.detail}
                      </span>
                    ))}
                  </div>
                </li>
              ))}
              {problemPages.length === 0 && (
                <li className="text-sm text-muted-foreground">Allt rent i det här intervallet.</li>
              )}
            </ul>
          </div>
        </section>
      )}
    </main>
  );
}
